import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  // Theme options
  themeOptions = [
    { label: 'Light', value: 'light' },
    { label: 'Dark', value: 'dark' },
    { label: 'Auto', value: 'auto' }
  ];
  selectedTheme: string = 'light';

  // Language options
  languageOptions = [
    { label: 'English', value: 'en' },
    { label: 'Spanish', value: 'es' },
    { label: 'French', value: 'fr' },
    { label: 'German', value: 'de' }
  ];
  selectedLanguage: string = 'en';

  // Notification settings
  emailNotifications: boolean = true;
  pushNotifications: boolean = true;
  smsNotifications: boolean = false;

  constructor(private messageService: MessageService) {}

  ngOnInit(): void {
    this.loadSettings();
  }

  loadSettings(): void {
    // Load saved settings from localStorage or service
    const savedTheme = localStorage.getItem('selectedTheme');
    if (savedTheme) {
      this.selectedTheme = savedTheme;
    }

    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      this.selectedLanguage = savedLanguage;
    }

    const savedEmailNotifications = localStorage.getItem('emailNotifications');
    if (savedEmailNotifications !== null) {
      this.emailNotifications = JSON.parse(savedEmailNotifications);
    }

    const savedPushNotifications = localStorage.getItem('pushNotifications');
    if (savedPushNotifications !== null) {
      this.pushNotifications = JSON.parse(savedPushNotifications);
    }

    const savedSmsNotifications = localStorage.getItem('smsNotifications');
    if (savedSmsNotifications !== null) {
      this.smsNotifications = JSON.parse(savedSmsNotifications);
    }
  }

  saveGeneralSettings(): void {
    // Save settings to localStorage
    localStorage.setItem('selectedTheme', this.selectedTheme);
    localStorage.setItem('selectedLanguage', this.selectedLanguage);
    localStorage.setItem('emailNotifications', JSON.stringify(this.emailNotifications));
    localStorage.setItem('pushNotifications', JSON.stringify(this.pushNotifications));
    localStorage.setItem('smsNotifications', JSON.stringify(this.smsNotifications));

    // Show success message
    this.messageService.add({
      key: 'main',
      severity: 'success',
      summary: 'Settings Saved',
      detail: 'Your settings have been saved successfully.',
      life: 3000
    });

    // Apply theme if changed
    this.applyTheme();
  }

  applyTheme(): void {
    // Apply the selected theme
    const body = document.body;

    // Remove existing theme classes
    body.classList.remove('light-theme', 'dark-theme');

    // Add new theme class
    if (this.selectedTheme === 'dark') {
      body.classList.add('dark-theme');
    } else if (this.selectedTheme === 'light') {
      body.classList.add('light-theme');
    } else {
      // Auto theme - use system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        body.classList.add('dark-theme');
      } else {
        body.classList.add('light-theme');
      }
    }
  }

  refreshSettings(): void {
    this.loadSettings();
    this.messageService.add({
      key: 'main',
      severity: 'info',
      summary: 'Settings Refreshed',
      detail: 'Settings have been refreshed successfully.',
      life: 3000
    });
  }

  saveAllSettings(): void {
    this.saveGeneralSettings();
    this.messageService.add({
      key: 'main',
      severity: 'success',
      summary: 'All Settings Saved',
      detail: 'All your settings have been saved successfully.',
      life: 3000
    });
  }

  exportSettings(): void {
    const settings = {
      theme: this.selectedTheme,
      language: this.selectedLanguage,
      emailNotifications: this.emailNotifications,
      pushNotifications: this.pushNotifications,
      smsNotifications: this.smsNotifications
    };

    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'settings-export.json';
    link.click();
    URL.revokeObjectURL(url);

    this.messageService.add({
      key: 'main',
      severity: 'success',
      summary: 'Settings Exported',
      detail: 'Your settings have been exported successfully.',
      life: 3000
    });
  }

  resetPreferences(): void {
    this.messageService.add({
      key: 'main',
      severity: 'info',
      summary: 'Reset Preferences',
      detail: 'Preferences reset functionality would be implemented here.',
      life: 3000
    });
    // TODO: Implement preferences reset logic
    console.log('Resetting preferences...');
  }

  changePassword(): void {
    this.messageService.add({
      key: 'main',
      severity: 'info',
      summary: 'Change Password',
      detail: 'Password change dialog would be displayed here.',
      life: 3000
    });
    // TODO: Implement password change dialog
    console.log('Changing password...');
  }

  viewActivity(): void {
    this.messageService.add({
      key: 'main',
      severity: 'info',
      summary: 'View Activity',
      detail: 'Account activity log would be displayed here.',
      life: 3000
    });
    // TODO: Navigate to activity log page
    console.log('Viewing activity...');
  }
}
